import { useState, useEffect, useRef } from 'react';
import { useProviderAuth } from '../../contexts/ProviderAuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Progress } from '../ui/progress';
import { 
  MessageCircle, 
  Send, 
  Loader2, 
  CheckCircle2, 
  AlertCircle,
  ArrowRight,
  Building2,
  MapPin,
  Users,
  Euro,
  Languages,
  Sparkles,
  Edit3,
  Save,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Circle,
  Phone,
  Mail,
  Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Conversational Provider Onboarding
 * 
 * A chat-guided interface that helps providers describe their business
 * naturally and extracts structured data behind the scenes.
 */
export function ProviderOnboarding({ onComplete, onSkip }) {
  const { token, user } = useProviderAuth();
  
  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [phase, setPhase] = useState('intro');
  const [messages, setMessages] = useState([]);
  const [extracted, setExtracted] = useState({});
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [missingRequired, setMissingRequired] = useState([]);
  const [readyForReview, setReadyForReview] = useState(false);
  
  // Vertical Schema state
  const [verticalFamily, setVerticalFamily] = useState(null);
  const [verticalSlug, setVerticalSlug] = useState(null);
  const [verticalConfidence, setVerticalConfidence] = useState('low');
  const [verticalAlternatives, setVerticalAlternatives] = useState([]);
  const [verticalFields, setVerticalFields] = useState({});
  const [liveCompleteness, setLiveCompleteness] = useState(null);
  
  // UI state
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showProgress, setShowProgress] = useState(true);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Check for active session or start new one
  useEffect(() => {
    checkOrStartSession();
  }, []);
  
  const checkOrStartSession = async () => {
    try {
      setLoading(true);
      
      // Check for active session
      const checkRes = await fetch(`${API_URL}/api/provider/onboarding/active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (checkRes.ok) {
        const data = await checkRes.json();
        
        if (data.has_active_session) {
          // Resume existing session
          await resumeSession(data.session_id);
          return;
        }
      }
      
      // Start new session
      await startNewSession();
      
    } catch (err) {
      setError('Errore di connessione. Riprova.');
      console.error('Session check error:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const startNewSession = async () => {
    const res = await fetch(`${API_URL}/api/provider/onboarding/start`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!res.ok) throw new Error('Failed to start session');
    
    const data = await res.json();
    setSessionId(data.session_id);
    setPhase(data.phase);
    setMessages([{ role: 'assistant', content: data.message }]);
    setExtracted(data.extracted || {});
    setCompletionPercentage(data.completion_percentage || 0);
    // Vertical Schema
    setVerticalFamily(data.vertical_family || null);
    setVerticalSlug(data.vertical_slug || null);
    setVerticalFields(data.vertical_fields || {});
  };
  
  const resumeSession = async (sid) => {
    const res = await fetch(`${API_URL}/api/provider/onboarding/${sid}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (!res.ok) throw new Error('Failed to resume session');
    
    const data = await res.json();
    setSessionId(data.session_id);
    setPhase(data.phase);
    setMessages(data.messages || []);
    setExtracted(data.extracted || {});
    setCompletionPercentage(data.completion_percentage || 0);
    setMissingRequired(data.missing_required || []);
    // Vertical Schema
    setVerticalFamily(data.vertical_family || null);
    setVerticalSlug(data.vertical_slug || null);
    setVerticalConfidence(data.vertical_confidence || 'low');
    setVerticalAlternatives(data.vertical_alternatives || []);
    setVerticalFields(data.vertical_fields || {});
    setLiveCompleteness(data.live_completeness || null);
    
    if (data.completion_percentage >= 70) {
      setReadyForReview(true);
    }
  };
  
  const sendMessage = async () => {
    if (!inputValue.trim() || !sessionId || loading) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    setLoading(true);
    setError(null);
    
    // Optimistically add user message
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    
    try {
      const res = await fetch(`${API_URL}/api/provider/onboarding/${sessionId}/message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: userMessage })
      });
      
      if (!res.ok) throw new Error('Failed to send message');
      
      const data = await res.json();
      
      // Add assistant response
      setMessages(prev => [...prev, { role: 'assistant', content: data.message }]);
      setPhase(data.phase);
      setExtracted(data.extracted || {});
      setCompletionPercentage(data.completion_percentage || 0);
      setMissingRequired(data.missing_required || []);
      setReadyForReview(data.ready_for_review || false);
      // Vertical Schema
      setVerticalFamily(data.vertical_family || null);
      setVerticalSlug(data.vertical_slug || null);
      setVerticalConfidence(data.vertical_confidence || 'low');
      setVerticalAlternatives(data.vertical_alternatives || []);
      setVerticalFields(data.vertical_fields || {});
      setLiveCompleteness(data.live_completeness || null);
      
    } catch (err) {
      setError('Errore invio messaggio. Riprova.');
      console.error('Send message error:', err);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  const goToReview = () => {
    setShowReview(true);
  };
  
  const saveProfile = async () => {
    if (!sessionId || saving) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const res = await fetch(`${API_URL}/api/provider/onboarding/${sessionId}/save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!res.ok) throw new Error('Failed to save profile');
      
      const data = await res.json();
      
      // Call completion callback
      if (onComplete) {
        onComplete(data);
      }
      
    } catch (err) {
      setError('Errore salvataggio. Riprova.');
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };
  
  // Helper: Get status color and label
  const getStatusInfo = (status) => {
    switch (status) {
      case 'ready_for_activation':
        return { color: 'bg-green-100 text-green-800 border-green-300', label: 'Pronto!', icon: CheckCircle2 };
      case 'ready_for_review':
        return { color: 'bg-amber-100 text-amber-800 border-amber-300', label: 'Quasi completo', icon: Clock };
      default:
        return { color: 'bg-gray-100 text-gray-600 border-gray-300', label: 'In corso...', icon: Circle };
    }
  };
  
  // Loading state
  if (loading && messages.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-umbria-olive" />
      </div>
    );
  }
  
  // Review screen
  if (showReview) {
    return (
      <ReviewScreen 
        extracted={extracted}
        completionPercentage={completionPercentage}
        missingRequired={missingRequired}
        onBack={() => setShowReview(false)}
        onSave={saveProfile}
        saving={saving}
        error={error}
      />
    );
  }
  
  // Chat interface
  return (
    <div className="flex flex-col h-[600px] max-h-[80vh] bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-umbria-olive to-umbria-olive/80 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold">Racconta la tua attività</h3>
              <p className="text-sm text-white/80">Compila il profilo conversando</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm font-medium">{completionPercentage}%</div>
              <Progress value={completionPercentage} className="w-20 h-2 bg-white/20" />
            </div>
            
            {readyForReview && (
              <Button 
                variant="secondary" 
                size="sm"
                onClick={goToReview}
                className="bg-white text-umbria-olive hover:bg-white/90"
              >
                Rivedi
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* Live Completeness Panel - Lightweight UX */}
      {(liveCompleteness || verticalFamily) && (
        <div className="border-b bg-gradient-to-r from-gray-50 to-white">
          {/* Collapsed summary - always visible */}
          <button 
            onClick={() => setShowProgress(!showProgress)}
            className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-gray-100/50 transition-colors"
            data-testid="progress-toggle"
          >
            <div className="flex items-center gap-3">
              {/* Family/Vertical indicator */}
              {liveCompleteness?.family?.label ? (
                <Badge className="gap-1.5 bg-umbria-olive/10 text-umbria-olive border-umbria-olive/20 font-medium">
                  <Sparkles className="w-3 h-3" />
                  {liveCompleteness.family.label}
                  {liveCompleteness.vertical?.confirmed && liveCompleteness.vertical?.label && (
                    <span className="opacity-70">• {liveCompleteness.vertical.label}</span>
                  )}
                </Badge>
              ) : verticalFamily ? (
                <Badge className="gap-1.5 bg-gray-100 text-gray-700 border-gray-200">
                  <HelpCircle className="w-3 h-3" />
                  {verticalConfidence === 'medium' ? 'Da confermare...' : 'Riconoscimento...'}
                </Badge>
              ) : null}
              
              {/* Status badge */}
              {liveCompleteness && (
                <Badge 
                  variant="outline" 
                  className={`gap-1 ${getStatusInfo(liveCompleteness.status).color}`}
                >
                  {(() => {
                    const StatusIcon = getStatusInfo(liveCompleteness.status).icon;
                    return <StatusIcon className="w-3 h-3" />;
                  })()}
                  {getStatusInfo(liveCompleteness.status).label}
                </Badge>
              )}
            </div>
            
            <div className="flex items-center gap-2 text-gray-500">
              <span className="text-xs">{showProgress ? 'Nascondi' : 'Dettagli'}</span>
              {showProgress ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </button>
          
          {/* Expanded progress details */}
          {showProgress && liveCompleteness && (
            <div className="px-4 pb-3 space-y-3 animate-in slide-in-from-top-2 duration-200">
              {/* Quick stats row */}
              <div className="flex items-center gap-4 text-xs text-gray-600">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span>{Object.keys(liveCompleteness.fields?.core_collected || {}).length + Object.keys(liveCompleteness.fields?.vertical_collected || {}).length} raccolti</span>
                </div>
                {(liveCompleteness.fields?.missing_core?.length > 0 || liveCompleteness.fields?.missing_vertical?.length > 0) && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-amber-400"></div>
                    <span>{(liveCompleteness.fields?.missing_core?.length || 0) + (liveCompleteness.fields?.missing_vertical?.length || 0)} mancanti</span>
                  </div>
                )}
                <div className="ml-auto flex items-center gap-1">
                  <Progress value={liveCompleteness.completion_percentage} className="w-16 h-1.5" />
                  <span className="font-medium">{liveCompleteness.completion_percentage}%</span>
                </div>
              </div>
              
              {/* Collected fields - show key ones */}
              {Object.keys(liveCompleteness.fields?.core_collected || {}).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(liveCompleteness.fields.core_collected).slice(0, 6).map(([key, value]) => (
                    <span 
                      key={key} 
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded-full border border-green-200"
                    >
                      <CheckCircle2 className="w-3 h-3" />
                      {typeof value === 'string' ? (value.length > 20 ? value.slice(0, 20) + '...' : value) : key.replace(/_/g, ' ')}
                    </span>
                  ))}
                  {Object.keys(liveCompleteness.fields.core_collected).length > 6 && (
                    <span className="text-xs text-gray-500 px-2">
                      +{Object.keys(liveCompleteness.fields.core_collected).length - 6} altri
                    </span>
                  )}
                </div>
              )}
              
              {/* Missing required - friendly hint */}
              {liveCompleteness.fields?.missing_core?.length > 0 && (
                <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    <span className="font-medium">Manca ancora:</span>{' '}
                    {liveCompleteness.fields.missing_core.slice(0, 3).join(', ')}
                    {liveCompleteness.fields.missing_core.length > 3 && ` (+${liveCompleteness.fields.missing_core.length - 3})`}
                  </span>
                </div>
              )}
              
              {/* Confidence clarification for medium */}
              {verticalConfidence === 'medium' && verticalAlternatives?.length > 0 && (
                <div className="flex items-start gap-2 text-xs text-blue-700 bg-blue-50 rounded-lg px-3 py-2">
                  <HelpCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    Chiara ti chiederà di confermare se sei in <strong>{liveCompleteness?.family?.label}</strong>{' '}
                    {verticalAlternatives.length > 0 && `o forse ${verticalAlternatives.map(a => a.replace(/_/g, ' ')).join(', ')}`}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, idx) => (
          <div 
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div 
              className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                msg.role === 'user' 
                  ? 'bg-umbria-olive text-white rounded-br-md' 
                  : 'bg-gray-100 text-gray-800 rounded-bl-md'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}
        
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
              <Loader2 className="w-5 h-5 animate-spin text-umbria-olive" />
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Error */}
      {error && (
        <div className="px-4 py-2 bg-red-50 text-red-600 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}
      
      {/* Input */}
      <div className="p-4 border-t bg-white">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Racconta della tua attività..."
            className="flex-1"
            disabled={loading}
            data-testid="onboarding-input"
          />
          <Button 
            onClick={sendMessage}
            disabled={!inputValue.trim() || loading}
            className="bg-umbria-olive hover:bg-umbria-olive/90"
            data-testid="onboarding-send"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        
        {onSkip && (
          <button 
            onClick={onSkip}
            className="mt-3 text-sm text-gray-500 hover:text-gray-700 w-full text-center"
          >
            Preferisci la compilazione classica? Vai al form
          </button>
        )}
      </div>
    </div>
  );
}


/**
 * Review Screen - Shows extracted data before saving
 */
function ReviewScreen({ extracted, completionPercentage, missingRequired, onBack, onSave, saving, error }) {
  
  const fieldGroups = [
    {
      title: "Informazioni Base",
      icon: Building2,
      fields: [
        { key: 'business_name', label: 'Nome attività' },
        { key: 'type', label: 'Tipo' },
        { key: 'subtype', label: 'Sottotipo' },
        { key: 'comune', label: 'Comune' },
        { key: 'macro_area', label: 'Zona' },
      ]
    },
    {
      title: "Descrizione",
      icon: Edit3,
      fields: [
        { key: 'short_description', label: 'Descrizione breve' },
        { key: 'long_description', label: 'Descrizione completa' },
        { key: 'services_offered', label: 'Servizi', isArray: true },
      ]
    },
    {
      title: "Target & Stile",
      icon: Users,
      fields: [
        { key: 'good_for', label: 'Adatto a', isArray: true },
        { key: 'styles', label: 'Stile', isArray: true },
        { key: 'family_friendly', label: 'Family friendly', isBoolean: true },
      ]
    },
    {
      title: "Info Pratiche",
      icon: Languages,
      fields: [
        { key: 'price_band', label: 'Fascia prezzo' },
        { key: 'languages_spoken', label: 'Lingue', isArray: true },
        { key: 'operating_hours', label: 'Orari' },
        { key: 'special_services', label: 'Servizi speciali', isArray: true },
      ]
    },
    {
      title: "Contatti",
      icon: MessageCircle,
      fields: [
        { key: 'contact_email', label: 'Email' },
        { key: 'contact_phone', label: 'Telefono' },
        { key: 'website', label: 'Sito web' },
      ]
    },
  ];
  
  const renderValue = (field) => {
    const value = extracted[field.key];
    
    if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
      return <span className="text-gray-400 italic">Non specificato</span>;
    }
    
    if (field.isArray && Array.isArray(value)) {
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((v, i) => (
            <Badge key={i} variant="secondary" className="text-xs">{v}</Badge>
          ))}
        </div>
      );
    }
    
    if (field.isBoolean) {
      return value ? (
        <Badge className="bg-green-100 text-green-700">Sì</Badge>
      ) : (
        <Badge variant="outline">No</Badge>
      );
    }
    
    return <span className="text-gray-800">{value}</span>;
  };
  
  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-umbria-olive to-umbria-olive/80 text-white p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
            <Sparkles className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Rivedi la tua scheda</h2>
            <p className="text-white/80">Controlla i dati prima di salvare</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="flex justify-between text-sm mb-1">
              <span>Completamento</span>
              <span className="font-medium">{completionPercentage}%</span>
            </div>
            <Progress value={completionPercentage} className="h-2 bg-white/20" />
          </div>
          
          {completionPercentage >= 70 ? (
            <Badge className="bg-green-500 text-white">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Pronto
            </Badge>
          ) : (
            <Badge className="bg-yellow-500 text-white">
              <AlertCircle className="w-3 h-3 mr-1" />
              Incompleto
            </Badge>
          )}
        </div>
      </div>
      
      {/* Missing fields warning */}
      {missingRequired.length > 0 && (
        <div className="mx-6 mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">Campi da completare</p>
              <p className="text-sm text-yellow-700">
                {missingRequired.join(', ')}
              </p>
              <p className="text-xs text-yellow-600 mt-1">
                Potrai completarli nella dashboard dopo il salvataggio.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Field groups */}
      <div className="p-6 space-y-6 max-h-[50vh] overflow-y-auto">
        {fieldGroups.map((group, idx) => (
          <div key={idx}>
            <div className="flex items-center gap-2 mb-3">
              <group.icon className="w-4 h-4 text-umbria-olive" />
              <h3 className="font-medium text-gray-800">{group.title}</h3>
            </div>
            
            <div className="grid gap-2">
              {group.fields.map((field) => (
                <div 
                  key={field.key}
                  className="flex justify-between items-start py-2 border-b border-gray-100 last:border-0"
                >
                  <span className="text-sm text-gray-500">{field.label}</span>
                  <div className="text-sm text-right max-w-[60%]">
                    {renderValue(field)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      {/* Error */}
      {error && (
        <div className="mx-6 mb-4 p-3 bg-red-50 text-red-600 text-sm flex items-center gap-2 rounded-lg">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}
      
      {/* Actions */}
      <div className="p-6 border-t bg-gray-50 flex gap-3">
        <Button 
          variant="outline" 
          onClick={onBack}
          className="flex-1"
          disabled={saving}
        >
          Torna alla chat
        </Button>
        
        <Button 
          onClick={onSave}
          disabled={saving}
          className="flex-1 bg-umbria-olive hover:bg-umbria-olive/90"
          data-testid="save-profile-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Salvataggio...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Salva e vai alla dashboard
            </>
          )}
        </Button>
      </div>
    </div>
  );
}


export default ProviderOnboarding;
